import {
    IDynamicLinkedServiceTypeMetadata,
    ILinkedServiceTypeMetadata
} from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';

import {
    azureDefaultAuthRefTypes,
    CredentialTypeOptions,
    lakehouseAuthTypes,
    ModelConstants,
    RootFolderType,
    servicePrincipalCredentialTypes
} from 'shell-models-lib';
import {
    authenticationReferenceMethodOptions,
    cdcDestination,
    commonDeltaStoreWriteSettingControls,
    commonFileTypeStoreDeleteActivityReadSettings,
    commonFileTypeStoreGetMetadataActivityReadSettings,
    getBinaryStoreWrittingControls,
    getCommonBinaryStoreReadSettingControls,
    getCommonDeltaStoreReadSettingControls,
    getCommonFileTypeStoreReadSettingControls,
    getStoreWriteSettingControls
} from 'app/registries/consts/metadata-consts';
import { format } from 'app/shared/utils/string.util';
import { GlobalVariables } from 'app/services/globals';
import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import { IRadioOption } from 'app/interfaces/IRadioOption';
import { adfModelTypeMap } from '../consts/adf-model-type-map-const';
import { deltaMetadata } from 'app/registries/dataset/delta-metadata';
import { avro, cdm, delimited, delta, json, orc, parquet } from 'app/registries/dataflow/dataflow-file-format-metadata';

export type LakeHouseTableActionType = 'Append' | 'Overwrite';

export const workspaceIdControlMetadata: ControlMetadata = {
    type: 'parameterizable',
    child: {
        type: 'textbox',
        bind: 'workspaceId',
        required: true,
        validators: [{ type: 'required', errorMessage: '' }],
        label: stringResources.FabricWorkspaceId,
        isLabelVertical: true
    }
};

export const artifactIdControlMetadata: ControlMetadata = {
    type: 'parameterizable',
    child: {
        type: 'textbox',
        bind: 'artifactId',
        required: true,
        validators: [{ type: 'required', errorMessage: '' }],
        label: format(stringResources.ObjectIdLabel, stringResources.Lakehouse_DisplayText),
        isLabelVertical: true
    }
};

// doc link TBD
const blockSizeInMbDesc =
    format(stringResources.BlockSizeInMB_Description, stringResources.Lakehouse_DisplayText) +
    ' <a target="_blank" href="https://go.microsoft.com/fwlink/?linkid=2111570">' +
    stringResources.LearnMore +
    '</a>';

const tenantBase: ControlMetadata = {
    type: 'tenant-textbox',
    bind: 'tenant',
    validators: [{ type: 'required', errorMessage: '' }],
    isLabelVertical: true,
    label: stringResources.ModelName_Tenant,
    required: true
};

const tenantInput: ControlMetadata[] = [
    {
        type: 'parameterizable',
        child: tenantBase
    }
];

const servicePrincipalIdBase: ControlMetadata = {
    type: 'textbox',
    bind: 'servicePrincipalId',
    label: stringResources.ModelName_ServicePrincipalID,
    required: true,
    validators: [{ type: 'required', errorMessage: '' }],
    isLabelVertical: true
};

const servicePrincipalIdInput: ControlMetadata[] = [
    {
        type: 'parameterizable',
        child: servicePrincipalIdBase
    }
];

const servicePrincipalKeyPasswordBase: ControlMetadata = {
    type: 'password',
    label: stringResources.ModelName_ServicePrincipalKey,
    required: true,
    isLabelVertical: true,
    validators: [{ type: 'required', errorMessage: '' }]
};

const servicePrincipalCredTypeInput: ControlMetadata[] = [
    {
        type: 'radio-switch',
        bind: 'servicePrincipalCredentialType',
        nested: 1,
        label: stringResources.ModelName_ServicePrincipalCredentialType,
        isLabelVertical: true,
        options: [
            {
                label: stringResources.ModelName_ServicePrincipalKey,
                value: servicePrincipalCredentialTypes.ServicePrincipalKey
            },
            {
                label: stringResources.ModelName_ServicePrincipalCert,
                value: servicePrincipalCredentialTypes.ServicePrincipalCert
            }
        ],
        required: true,
        validators: [{ type: 'required', errorMessage: '' }],
        defaultValue: servicePrincipalCredentialTypes.ServicePrincipalKey,
        cases: [
            {
                caseValue: servicePrincipalCredentialTypes.ServicePrincipalKey,
                children: [
                    {
                        type: 'dynamic-akv-switcher',
                        bind: 'servicePrincipalKey',
                        label: stringResources.ModelName_ServicePrincipalKey,
                        enableAKVParameterization: true,
                        child: {
                            type: 'parameterizable',
                            attribute: 'servicePrincipalKey',
                            child: servicePrincipalKeyPasswordBase,
                            disableAddParameter: true
                        },
                        description: format(
                            stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                            format(
                                stringResources.ModelName_LinkedServiceTemplate,
                                stringResources.Lakehouse_DisplayText
                            ),
                            stringResources.ModelName_ServicePrincipalKey
                        )
                    }
                ]
            },
            {
                caseValue: servicePrincipalCredentialTypes.ServicePrincipalCert,
                children: [
                    {
                        type: 'dynamic-akv-switcher',
                        bind: 'servicePrincipalCert',
                        label: stringResources.ModelName_ServicePrincipalCert,
                        enableAKVParameterization: true,
                        validators: [{ type: 'required', errorMessage: '' }],
                        akvOnly: true,
                        isCert: true,
                        akvOptionDisplayText: stringResources.AzureKeyVault_ServicePrincipalCert_DisplayText,
                        description: format(
                            stringResources.AzureKeyVaultCertName_Tooltip_Template,
                            format(
                                stringResources.ModelName_LinkedServiceTemplate,
                                stringResources.Lakehouse_DisplayText
                            ),
                            stringResources.ModelName_ServicePrincipalCert
                        )
                    }
                ]
            }
        ]
    }
];

const servicePrincipalInlineMethodChildren: ControlMetadata[] = [
    // service Principal
    ...tenantInput,
    ...servicePrincipalIdInput,
    ...servicePrincipalCredTypeInput
];

// provides user with inline vs credential radio button to add authentication parameters for SAMI and SP Authentication methods.
export function authReferenceSetting(credentialTypeFilter: CredentialTypeOptions): ControlMetadata {
    return {
        type: 'radio-switch',
        bind: 'authReferenceMethod',
        nested: 1,
        label: stringResources.Authentication_Reference_Method_Label,
        description: stringResources.Authentication_Reference_Method_Description,
        options: authenticationReferenceMethodOptions,
        isLabelVertical: true,
        defaultValue: azureDefaultAuthRefTypes.InlineRefTypeAuthentication,
        cases: [
            {
                caseValue: azureDefaultAuthRefTypes.InlineRefTypeAuthentication,
                children:
                    credentialTypeFilter === CredentialTypeOptions.ServicePrincipal
                        ? servicePrincipalInlineMethodChildren
                        : [
                              // system Assigned Managed Identity
                              {
                                  type: 'management-identity-info',
                                  resourceType: stringResources.Lakehouse_DisplayText,
                                  link: 'https://docs.microsoft.com/azure/data-factory/connector-azure-blob-storage#managed-identity'
                              }
                          ]
            },
            {
                caseValue: azureDefaultAuthRefTypes.CredentialRefTypeAuthentication,
                children: [
                    {
                        type: 'credential-select',
                        bind: 'credential',
                        filterCredentialType: credentialTypeFilter,
                        required: true,
                        validators: [{ type: 'required', errorMessage: '' }]
                    }
                ]
            }
        ]
    };
}

export const getAuthOptions = () => {
    let options: Array<{ label: string; value: string }> = [
        {
            label: stringResources.AuthenticationType_ServicePrincipal,
            value: lakehouseAuthTypes.ServicePrincipalAuthentication
        },
        {
            label: stringResources.AuthenticationType_ManagedServiceIdentity,
            value: lakehouseAuthTypes.MSIAuthentication
        },
        {
            label: stringResources.AuthenticationType_UserAssignedManagedIdentity,
            value: lakehouseAuthTypes.UserAssignedManagedIdentity
        }
    ];

    if (GlobalVariables.lakehouseUserAuthEnabled) {
        options = options.concat([
            {
                label: stringResources.ModelName_UserAuthentication_NoPreview,
                value: lakehouseAuthTypes.UserAuth
            }
        ]);
    }

    return options;
};

export const lakehouseOptions: Array<IRadioOption<RootFolderType>> = [
    {
        label: stringResources.Tables,
        value: 'Tables',
        disabledTooltip: stringResources.CopyWizard_Dataset_Lakehouse_RootFolderIsDisabled_Tooltip
    },
    {
        label: stringResources.Files,
        value: 'Files'
    }
];

const lakehouseBaseCDCSettings: ILinkedServiceTypeMetadata['cdc'] = {
    supportCDC: true,
    supportedCDCDestinations: cdcDestination,
    notSupportTableDetection: true
};

export const lakehouseTableCDCSettings: ILinkedServiceTypeMetadata['cdc'] = lakehouseBaseCDCSettings;

export const lakehouseFileCDCSettings: ILinkedServiceTypeMetadata['cdc'] | undefined =
    GlobalVariables.copyJobGenericCdc
        ? { ...lakehouseBaseCDCSettings, supportGenericCdc: true }
        : undefined;

export const lakehouseMetadata: IDynamicLinkedServiceTypeMetadata = async (injector) => {
    const metadata: ILinkedServiceTypeMetadata = {
        type: adfModelTypeMap.lakehouse.linkedServiceType,
        displayName: __IS_FABRIC_DPE__
            ? stringResources.Lakehouse_DisplayText
            : stringResources.Lakehouse_Fabric_DisplayText,
        displayNameForDataset: __IS_FABRIC_DPE__ ? undefined : stringResources.LakehouseFiles_DisplayText,
        supportedInTrident: true,
        fabricArtifactType: ModelConstants.lakehouseArtifactType,
        supportCopyJobSource: true,
        supportCopyJobSink: true,
        supportMultipleIncrementalColumns: true,
        linkedServiceKind: 'file',
        icon: {
            className: 'svg-i_dataLakehouse'
        },
        dmtsCredentialConnectionType: adfModelTypeMap.lakehouseCredential.linkedServiceType,
        isFirstClass: true,
        category: __IS_FABRIC_DPE__ ? ['Workspace'] : ['File'],
        supportRootFolder: true,
        copySinkTypes: [],
        copySourceTypes: [],
        supportDataFlowSink: true,
        supportAutoFillCopilotInput: true,
        isKnownInPipelineContext: true,
        dataflowSink: [avro, cdm, delimited, delta, json, orc, parquet],
        supportDataFlowSource: true,
        dataflowSource: [avro, cdm, delimited, delta, json, orc, parquet],
        supportFirstClassParameterExperience: true,
        supportIncrementalBinaryCopy: true,
        supportDatasetV2: true,
        locationPattern: {
            folderPath: '([^]*)/[^/]*',
            fileName: '(?:[^]*/)?([^/]*)'
        },
        mergeReadSettingsInTrident: true,
        locationPropertiesRequireParameters: ['folderPath', 'fileName'],
        supportGetMetadataActivity: true,
        getMetadataActivityReadProperties: commonFileTypeStoreGetMetadataActivityReadSettings,
        deleteActivityReadProperties: commonFileTypeStoreDeleteActivityReadSettings,
        storeReadSettingControls: getCommonFileTypeStoreReadSettingControls(),
        // propertyBag is LakehouseWriteSettings, while rootFolder is a property of Dataset (Trident) / LinkedService (ADF)
        storeWriteSettingControls: getStoreWriteSettingControls(__IS_FABRIC_DPE__, blockSizeInMbDesc, undefined),
        datasetTypeToStoreReadSettingControls: {
            [ModelConstants.binaryDataset]: getCommonBinaryStoreReadSettingControls(),
            [deltaMetadata.type]: getCommonDeltaStoreReadSettingControls()
        },
        datasetTypeToStoreWriteSettingControls: {
            [ModelConstants.binaryDataset]: getBinaryStoreWrittingControls(blockSizeInMbDesc),
            [deltaMetadata.type]: [...commonDeltaStoreWriteSettingControls],
            [ModelConstants.jsonDataset]: getStoreWriteSettingControls(
                __IS_FABRIC_DPE__,
                blockSizeInMbDesc,
                ModelConstants.jsonDataset
            )
        },
        controls: [
            {
                type: 'radio-switch',
                nested: 1,
                label: format(stringResources.FabricArtifactSelectionMethod, stringResources.Lakehouse_DisplayText),
                options: [
                    {
                        value: 'selection',
                        label: stringResources.FromSelectionLabel
                    },
                    {
                        value: 'manual',
                        label: stringResources.EnterManuallyLabel
                    }
                ],
                isLabelVertical: true,
                defaultValue: '{workspaceId != null || parameterized ? "manual" : "selection"}',
                cases: [
                    {
                        caseValue: 'selection',
                        children: [
                            {
                                type: 'fabric-artifact-account-select',
                                bind: {
                                    workspaceID: 'workspaceId',
                                    artifactID: 'artifactId'
                                },
                                artifactType: ModelConstants.lakehouseArtifactType,
                                artifactTypePlural: 'lakehouses',
                                artifactIdLabel: stringResources.Lakehouse_Name_DisplayText,
                                showEndpoint: false,
                                required: true,
                                validators: [{ type: 'required', errorMessage: '' }],
                                isLabelVertical: true
                            }
                        ]
                    },
                    {
                        caseValue: 'manual',
                        children: [workspaceIdControlMetadata, artifactIdControlMetadata]
                    }
                ]
            },
            {
                type: 'select-switch',
                bind: 'authenticationMethod',
                label: stringResources.ModelName_AuthenticationType,
                options: getAuthOptions(),
                isLabelVertical: true,
                defaultValue: lakehouseAuthTypes.ServicePrincipalAuthentication,
                cases: [
                    {
                        caseValue: lakehouseAuthTypes.ServicePrincipalAuthentication,
                        children: [authReferenceSetting(CredentialTypeOptions.ServicePrincipal)]
                    },
                    {
                        caseValue: lakehouseAuthTypes.MSIAuthentication,
                        children: !GlobalVariables.isSamiSupportEnabled
                            ? [
                                  {
                                      type: 'management-identity-info',
                                      resourceType: stringResources.Lakehouse_DisplayText,
                                      link: 'https://learn.microsoft.com/azure/data-factory/connector-microsoft-fabric-lakehouse?tabs=data-factory#linked-service-properties'
                                  }
                              ]
                            : [authReferenceSetting(CredentialTypeOptions.ManagedIdentity)]
                    },
                    {
                        // for UAMI there won't be any radio buttons, since inline mode is not supported.
                        caseValue: lakehouseAuthTypes.UserAssignedManagedIdentity,
                        children: [
                            {
                                type: 'credential-select',
                                bind: 'credential',
                                filterCredentialType: CredentialTypeOptions.UserAssignedManagedIdentity,
                                required: true,
                                validators: [{ type: 'required', errorMessage: '' }]
                            }
                        ]
                    },
                    {
                        caseValue: lakehouseAuthTypes.UserAuth,
                        children: [
                            {
                                type: 'credential-select',
                                bind: 'credential',
                                hide: !GlobalVariables.enableConnectorUserAuthCred,
                                filterCredentialType: CredentialTypeOptions.UserAuth,
                                required: true,
                                validators: [{ type: 'required', errorMessage: '' }]
                            }
                        ]
                    }
                ]
            }
        ]
    };

    return metadata;
};
